import List from '@mui/material/List'
import Typography from '@mui/material/Typography'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { useDeleteVacationHoldMutation, useLazyGetVacationHoldListQuery } from 'api/accounts/vacationHoldsCrud'
import HHAccordion from 'components/common/HHAccordion'
import HHConfirmDialog from 'components/common/HHConfirmDialog'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import HoldActionMenu from 'components/customer-details/content/holds/HoldActionMenu'
import HoldListItem from 'components/customer-details/content/holds/HoldListItem'
import HoldsFormDialog from 'components/customer-details/content/holds/HoldsFormDialog'
import SkeletonHoldListItem from 'components/customer-details/content/holds/SkeletonHoldListItem'
import { getCustomerDetails } from 'middleware/actions/customers'
import { HOLD_FORM_MODES } from 'settings/constants/service'
import T from 'T'
import { formatDateToFEFormat } from 'utils/date'
import { handleError } from 'utils/error'

import type { HoldFormMode, HoldsRootState } from './types'
import type { VacationHoldApi } from 'types/api/account/vacationHold'

type Props = {
  accountId: string
}

const CONFIRM_BUTTON_PROPS_IDLE = { loading: false }
const CONFIRM_BUTTON_PROPS_LOADING = { loading: true }
const CONFIRM_DIALOG_PROPS = { maxWidth: 'xs', fullWidth: true } as const

const describeDates = ({ startDate, endDate }: VacationHoldApi): string => {
  const start = formatDateToFEFormat(startDate)
  const end = formatDateToFEFormat(endDate)
  return start === end ? start : `${start} - ${end}`
}

const HoldsAccordion: React.FC<Props> = ({ accountId }) => {
  const dispatch = useDispatch()
  const [formMode, setFormMode] = useState<HoldFormMode>(HOLD_FORM_MODES.CREATE)
  const [openFormDialog, setOpenFormDialog] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedHold, setSelectedHold] = useState<VacationHoldApi | null>(null)
  const [getVacationHoldList, { data, isLoading, isFetching, isSuccess }] = useLazyGetVacationHoldListQuery()
  const [deleteHold, { isLoading: isDeleteLoading }] = useDeleteVacationHoldMutation()
  const loading = isLoading || isFetching
  const selectedLocationId = useSelector((state: HoldsRootState) => state.CustomerDetails?.selectedLocationId ?? '')
  const customerDetails = useSelector((state: HoldsRootState) => state.CustomersReducer?.customerDetails ?? null, shallowEqual)

  const holds = useMemo(() => {
    const allHolds = data?.vacationHolds ?? []
    if (!selectedLocationId) return allHolds
    return allHolds.filter(hold => hold.locations?.includes(selectedLocationId))
  }, [data, selectedLocationId])

  const handleCloseDeleteDialog = useCallback(() => setOpenDeleteDialog(false), [])

  const handleConfirmDelete = useCallback(() => {
    if (selectedHold) {
      deleteHold({ vacationHoldId: selectedHold.id, accountId })
        .unwrap()
        .then(() => dispatch(getCustomerDetails({ accountId })))
        .catch(handleError)
    }
    setOpenDeleteDialog(false)
  }, [accountId, deleteHold, dispatch, selectedHold])

  const handleAddClick = useCallback(() => {
    setFormMode(HOLD_FORM_MODES.CREATE)
    setOpenFormDialog(true)
  }, [])

  const handleFormDialogClose = useCallback(() => {
    setOpenFormDialog(false)
    setFormMode(HOLD_FORM_MODES.CREATE)
  }, [])

  const handleOpenActionMenu = useCallback((event: React.MouseEvent<HTMLElement>, hold: VacationHoldApi) => {
    setSelectedHold(hold)
    setAnchorEl(event.currentTarget)
  }, [])

  const handleCloseActionMenu = useCallback(() => setAnchorEl(null), [])

  const handleEditActionClick = useCallback(() => {
    setFormMode(HOLD_FORM_MODES.UPDATE)
    setOpenFormDialog(true)
  }, [])

  const handleDeleteActionClick = useCallback(() => setOpenDeleteDialog(true), [])

  useEffect(() => {
    if (!accountId) return
    getVacationHoldList({ accountId, deleted: false }).unwrap().catch(handleError)
  }, [accountId, getVacationHoldList])

  return (
    <HHAccordion
      isExpandCollapseHeightSame
      isExpanded
      showActionIcon
      withNewColors
      actionType="add"
      summary={T.HOLDS}
      onActionClick={handleAddClick}
    >
      {loading && (
        <List>
          <SkeletonHoldListItem divider />
          <SkeletonHoldListItem divider />
          <SkeletonHoldListItem />
        </List>
      )}
      {!loading && isSuccess && holds.length === 0 && <HHSectionPlaceholder title="Click the + button to add an account hold" />}
      {!loading && isSuccess && holds.length !== 0 && (
        <List>
          {holds.map((hold, index) => (
            <HoldListItem
              key={hold.id}
              divider={index < holds.length - 1}
              hold={hold}
              primary={hold.title}
              secondary={describeDates(hold)}
              onOpenActionMenu={handleOpenActionMenu}
            />
          ))}
        </List>
      )}
      <HoldActionMenu
        accountId={accountId}
        anchorEl={anchorEl}
        selectedHold={selectedHold}
        onClose={handleCloseActionMenu}
        onDelete={handleDeleteActionClick}
        onEdit={handleEditActionClick}
      />
      <HHConfirmDialog
        ConfirmButtonProps={isDeleteLoading ? CONFIRM_BUTTON_PROPS_LOADING : CONFIRM_BUTTON_PROPS_IDLE}
        confirmButtonTitle={T.DELETE}
        confirmDescription={<Typography component="p">{T.ARE_YOU_SURE_YOU_WANT_TO_CONTINUE}</Typography>}
        confirmTitle="Confirm hold deletion"
        DialogProps={CONFIRM_DIALOG_PROPS}
        isOpen={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
      />
      <HoldsFormDialog
        accountId={accountId}
        customerDetails={customerDetails}
        mode={formMode}
        open={openFormDialog}
        selectedHold={selectedHold}
        onClose={handleFormDialogClose}
        onSave={handleFormDialogClose}
      />
    </HHAccordion>
  )
}

export default HoldsAccordion
